import { useEffect, useState, useCallback } from "react";
import { getEpics, createEpic, updateEpic, deleteEpic } from "./EpicsService";
import { useToast } from "../ToastContext";
import EpicDetail from "./EpicDetail";
import EpicFormModal from "./EpicFormModal";
import "./EpicsStories.css";

const STATUS_META = {
  PLANNING:  { label: "Planning",   cls: "es-status-planning"  },
  ACTIVE:    { label: "Active",     cls: "es-status-active"    },
  ON_HOLD:   { label: "On Hold",    cls: "es-status-onhold"    },
  COMPLETED: { label: "Completed",  cls: "es-status-completed" },
  ARCHIVED:  { label: "Archived",   cls: "es-status-archived"  },
};

/**
 * Owns the Epic → Story → Task drill-down for a project's "Epics" tab.
 * Fully self-contained: ProjectDetail just mounts this once with the
 * project's id/name/color — no routing, no state lifted to the parent.
 */
export default function EpicsTab({ projectId, projectName, projectColor }) {
  const toast = useToast();
  const [epics, setEpics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEpic, setEditingEpic] = useState(null);
  const [openEpic, setOpenEpic] = useState(null); // drilled-in epic, or null = list view

  const load = useCallback(() => {
    setLoading(true);
    getEpics(projectId)
      .then(setEpics)
      .catch(() => toast("Failed to load epics", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    try {
      if (editingEpic) {
        const updated = await updateEpic(projectId, editingEpic.id, data);
        setEpics(es => es.map(e => e.id === updated.id ? updated : e));
        setOpenEpic(o => (o && o.id === updated.id ? updated : o));
        toast("Epic updated", "success");
      } else {
        const created = await createEpic(projectId, data);
        setEpics(es => [...es, created]);
        toast("Epic created", "success");
      }
      setShowForm(false);
      setEditingEpic(null);
    } catch {
      toast(editingEpic ? "Failed to update epic" : "Failed to create epic", "error");
    }
  };

  const handleDelete = async (epic) => {
    if (!window.confirm(`Delete epic "${epic.name}"? Its stories will be removed; tasks stay but lose their story link.`)) return;
    try {
      await deleteEpic(projectId, epic.id);
      setEpics(es => es.filter(e => e.id !== epic.id));
      toast("Epic deleted", "success");
    } catch {
      toast("Failed to delete epic", "error");
    }
  };

  return (
    <>
      {openEpic ? (
        <EpicDetail
          projectName={projectName}
          projectColor={projectColor}
          epic={openEpic}
          onBack={() => { setOpenEpic(null); load(); }}
          onEdit={() => { setEditingEpic(openEpic); setShowForm(true); }}
          onDelete={async () => { await handleDelete(openEpic); setOpenEpic(null); }}
        />
      ) : (
        <div className="es-epics-tab">
          <div className="es-section-header">
            <h4>Epics</h4>
            <button className="proj-btn-primary" onClick={() => { setEditingEpic(null); setShowForm(true); }}>+ New Epic</button>
          </div>

          {loading ? (
            <div className="es-empty">Loading epics…</div>
          ) : epics.length === 0 ? (
            <div className="es-empty">No epics yet. Break this project into epics to organize stories and tasks underneath.</div>
          ) : (
            <div className="es-epic-grid">
              {epics.map(e => (
                <div key={e.id} className="es-epic-card"
                     style={{ "--es-color": e.color || projectColor || "#6366f1" }}
                     onClick={() => setOpenEpic(e)}>
                  <div className="es-epic-card-top">
                    <span className="es-epic-icon">{e.icon || "🧩"}</span>
                    <span className="es-epic-name">{e.name}</span>
                    <span className={`es-status-badge ${STATUS_META[e.status]?.cls || ""}`}>
                      {STATUS_META[e.status]?.label || e.status}
                    </span>
                  </div>
                  {e.description && <div className="es-epic-desc">{e.description}</div>}
                  <div className="es-epic-stats">
                    <span>📖 {e.doneStories}/{e.totalStories} stories</span>
                    <span>✅ {e.doneTasks}/{e.totalTasks} tasks</span>
                    {e.commentCount > 0 && <span>💬 {e.commentCount}</span>}
                  </div>
                  <div className="es-epic-actions">
                    <button className="es-icon-btn" onClick={ev => { ev.stopPropagation(); setEditingEpic(e); setShowForm(true); }} title="Edit epic">✏️</button>
                    <button className="es-icon-btn es-icon-btn-del" onClick={ev => { ev.stopPropagation(); handleDelete(e); }} title="Delete epic">🗑</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <EpicFormModal
          epic={editingEpic}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingEpic(null); }}
        />
      )}
    </>
  );
}
